import { Table } from '@g17eco/molecules/table';
import { ColumnDef } from '@tanstack/react-table';
import { getCalendarDate } from '@utils/date';
import { getIconClass } from '@utils/files';
import { bytesToHumanReadable } from '@utils/index';
import { Button } from 'reactstrap';
import { DocumentMediaType, InitiativeDocument } from '@g17eco/types/document';

interface BaseDocumentProps {
  key: DocumentMediaType;
  title: string;
  icon: string;
  emptyText?: string;
}

interface MediaDocumentProps extends BaseDocumentProps {
  key: DocumentMediaType.Image | DocumentMediaType.Video;
  render: (media: InitiativeDocument, onImageClick?: () => void) => JSX.Element;
}

interface FileDocumentProps extends BaseDocumentProps {
  key: DocumentMediaType.File | DocumentMediaType.Assurance;
  render: (
    files: InitiativeDocument[],
    editHandler: (media: InitiativeDocument) => void,
    initiativeId: string,
    canEdit: boolean,
  ) => JSX.Element;
}

export type DocumentProps = MediaDocumentProps | FileDocumentProps;

export const isFileDocumentProps = (props: DocumentProps): props is FileDocumentProps => {
  return props.key === DocumentMediaType.File || props.key === DocumentMediaType.Assurance;
};

const renderVideo = (media: InitiativeDocument) => {
  return (
    <div key={'video-' + media._id} className='homepageMediaTile'>
      <div className='card-img-top mb-5'>
        <video controls title={media.title}>
          <source src={media.url} type={media.metadata.mimetype} />
        </video>
        <div className='card-body p-2'>
          <h6 className='card-title'>{media.title}</h6>
          <p className='card-text'>{media.description}</p>
        </div>
      </div>
    </div>
  );
};

const renderPhoto = (media: InitiativeDocument, onImageClick?: () => void) => {
  return (
    <div key={'photo-' + media._id} className='homepageMediaTile'>
      <div className='img-thumbnail'>
        <div className='type-container'>
          <i className={`fas ${getIconClass(media)}`} />
        </div>
        <div
          style={{
            backgroundImage: `url(${media.url})`
          }}
          onClick={onImageClick}
        >
          <div className='mediaTileText'>
            <h5>{media.title}</h5>
            <p>{media.description}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const renderFilesContent = (
  files: InitiativeDocument[],
  editHandler: (media: InitiativeDocument) => void,
  initiativeId: string,
  canEdit: boolean,
) => {
  const columns: ColumnDef<InitiativeDocument>[] = [
    {
      header: 'Title',
      accessorFn: (file: InitiativeDocument) => (file.title ? file.title : file.metadata.name),
    },
    {
      header: 'Type',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      accessorFn: (file: InitiativeDocument) => file.metadata.extension.toUpperCase(),
    },
    {
      header: 'Size',
      meta: {
        cellProps: {
          className: 'text-right',
        },
      },
      accessorFn: (file: InitiativeDocument) => bytesToHumanReadable(file.size),
    },
    {
      header: 'Date',
      accessorFn: (file: InitiativeDocument) => getCalendarDate(file.created),
    },
    {
      header: ' ',
      id: 'actions',
      meta: {
        cellProps: {
          className: 'text-right px-0',
        },
      },
      enableSorting: false,
      cell: ({ row }) => {
        const file = row.original;
        return (
          <>
            <Button color='link' className='p-0 mx-1' onClick={() => window.open(file.url)}>
              <i className='fas fa-download' />
            </Button>
            {!!canEdit && file.ownerId === initiativeId && (
              <Button color='link' className='p-0 mx-1' onClick={() => editHandler(file)}>
                <i className='fas fa-pen' />
              </Button>
            )}
          </>
        );
      },
    },
  ];

  return <Table data={files} className='mt-3' columns={columns} />;
};

export const documentProps: DocumentProps[] = [
  {
    key: DocumentMediaType.Image,
    title: 'Photo',
    icon: 'fa-image',
    render: renderPhoto,
  },
  {
    key: DocumentMediaType.Video,
    title: 'Video',
    icon: 'fa-video',
    render: renderVideo,
  },
  {
    key: DocumentMediaType.File,
    title: 'File',
    emptyText: 'documents',
    icon: 'fa-file',
    render: renderFilesContent,
  },
  {
    key: DocumentMediaType.Assurance,
    title: 'Assurance',
    emptyText: 'assurance files',
    icon: 'fa-award',
    render: renderFilesContent,
  },
];

// TODO: To support assurance documents and remove this function
export const getSupportedMediaType = (activeTab: DocumentMediaType) => {
  if (activeTab === DocumentMediaType.Assurance) {
    return undefined;
  }
  return activeTab;
};
